import { useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { TabBar } from '@/components/ui/TabBar';
import DispensingTab from './DispensingTab';
import InventoryTab from './InventoryTab';
import RegisterTab from './RegisterTab';
import ProcurementTab from './procurement/ProcurementTab';
import OutpatientTab from './outpatient/OutpatientTab';
import SalesReturnsTab from './outpatient/SalesReturnsTab';
import IpPharmacyScreen from './wardRequests/IpPharmacyScreen';
import RxHistoryTab from './RxHistoryTab';
import BorrowTab from './borrow/BorrowTab';
import NearExpiryTab from './NearExpiryTab';
import StockInsightsTab from './StockInsightsTab';
import TransfersTab from './transfers/TransfersTab';
import { StoreBadge } from './StoreBadge';
import type { PharmacyChannel } from './types';

/**
 * Fifth screen migrated off Create React App — see HMS_V4_API_CONTRACT.md
 * and QueueView.tsx for the established pattern: a drop-in replacement for
 * src/components/PharmacyModule.jsx, swapped into App.jsx by changing one
 * import line.
 *
 * By far the largest single screen migrated this session (4 tabs, 3
 * procurement sub-panels, ~15 distinct actions) — done as one unit rather
 * than split, unlike the Billing/Reports separation, because the doc lists
 * "Pharmacy" as a single Phase 4 item and every tab here is a genuinely
 * inseparable part of one pharmacist workflow, not two screens that happen
 * to share a file.
 *
 * Structural difference from the legacy version worth noting: the legacy
 * top level held one shared `error` string, set via a setError prop passed
 * down into whichever tab was mounted, and rendered as a single banner here.
 * Each tab below now owns its own error display next to its own mutation
 * (the same pattern every other migrated screen this session already uses),
 * so there is no shared error state left at this level — a natural
 * consequence of each tab managing its own React Query state rather than a
 * behaviour change deliberately chosen for its own sake. A Dispensing error
 * no longer lingers on screen after switching to the Inventory tab, which
 * previously it would have.
 */
export type PharmacyTab = 'dispensing' | 'outpatient' | 'sales' | 'ip-pharmacy' | 'inventory' | 'near-expiry' | 'insights' | 'transfers' | 'procurement' | 'borrow' | 'register' | 'rx-history';

/**
 * `module` marks a tab that needs a grant BEYOND the 'pharmacy' module this
 * whole screen already sits behind. Only the walk-in counter has one: it takes
 * money, applies discounts and issues refunds, whereas the Doctor and Nurse
 * roles that legitimately hold 'pharmacy' need Dispensing and Inventory and
 * have no business at a cash till. See migration_pharmacy_outpatient.sql.
 */
const TABS: Array<{ id: PharmacyTab; label: string; module?: string }> = [
  { id: 'dispensing', label: '💊 Dispensing' },
  { id: 'outpatient', label: '🧾 Outpatient', module: 'pharmacy:outpatient' },
  { id: 'sales', label: '↩ Sales & Returns', module: 'pharmacy:outpatient' },
  // Admission-scoped purchases (an attender buying medicine during a stay) —
  // same grant as the walk-in counter, since it's the identical "pharmacist
  // at a till" capability with a different customer lookup. See
  // migration_ip_pharmacy.sql / routes/ip-pharmacy.js.
  { id: 'ip-pharmacy', label: '🏨 IP Pharmacy', module: 'pharmacy:outpatient' },
  { id: 'inventory', label: '📦 Inventory' },
  // Stock inside the hospital's near-expiry window, plus the later-batch audit trail.
  { id: 'near-expiry', label: '⏳ Near Expiry' },
  // G16: reorder suggestions, FSN/ABC, unusual movements — read-only.
  { id: 'insights', label: '📈 Stock insights' },
  { id: 'transfers', label: '🔄 Store Transfers', module: 'pharmacy:transfer' },
  { id: 'procurement', label: '🚚 Procurement' },
  // Stock lent to another hospital and taken back / paid for — its own grant
  // for the same reason as the counter: it moves stock out and money in.
  { id: 'borrow', label: '🔁 Medicine Borrow & Return', module: 'pharmacy:borrow' },
  { id: 'register', label: '📋 Schedule Register' },
  { id: 'rx-history', label: '📜 Rx History' },
];

/** Which store a tab works on — shown as a badge once the branch has more than one store. */
const TAB_CHANNEL: Partial<Record<PharmacyTab, { channel: PharmacyChannel; verb: string }>> = {
  dispensing: { channel: 'visit', verb: 'Selling from' },
  outpatient: { channel: 'outpatient', verb: 'Selling from' },
  'ip-pharmacy': { channel: 'admission', verb: 'Selling from' },
  procurement: { channel: 'receiving', verb: 'Receiving into' },
  borrow: { channel: 'borrow', verb: 'Lending from' },
};

/** `initialTab` lets another screen open Pharmacy on a specific tab (the
 * Dashboard's stock alerts and the bell's stock items open Inventory). */
export default function PharmacyModule({ initialTab = 'dispensing' }: { initialTab?: PharmacyTab } = {}) {
  const { user } = useAuth();
  const [tab, setTab] = useState<PharmacyTab>(initialTab);

  // Hiding the tab is a convenience, not the control — every counter endpoint
  // re-checks 'pharmacy:outpatient' server-side on each request
  // (routes/pharmacy-outpatient.js), the same way requireModule re-queries
  // roles.modules rather than trusting a JWT claim.
  const visibleTabs = useMemo(
    () => TABS.filter((t) => !t.module || user?.modules?.includes(t.module)),
    [user?.modules]
  );

  return (
    <div className="fade-in">
      <TabBar tabs={visibleTabs} active={tab} onChange={setTab} className="mb-4" />
      {TAB_CHANNEL[tab] && (
        <div className="-mt-2 mb-3 flex justify-end empty:hidden">
          <StoreBadge channel={TAB_CHANNEL[tab]!.channel} verb={TAB_CHANNEL[tab]!.verb} />
        </div>
      )}
      {tab === 'dispensing' && <DispensingTab />}
      {tab === 'outpatient' && <OutpatientTab />}
      {tab === 'sales' && <SalesReturnsTab />}
      {tab === 'ip-pharmacy' && <IpPharmacyScreen />}
      {tab === 'inventory' && <InventoryTab />}
      {tab === 'near-expiry' && <NearExpiryTab />}
      {tab === 'insights' && <StockInsightsTab />}
      {tab === 'transfers' && <TransfersTab />}
      {tab === 'procurement' && <ProcurementTab />}
      {tab === 'borrow' && <BorrowTab />}
      {tab === 'register' && <RegisterTab />}
      {tab === 'rx-history' && <RxHistoryTab />}
    </div>
  );
}
